/**
 * Scoped CSS for the chat widget — EDITORIAL-NITIDO direction.
 *
 * Contrast and structure: defined 1px borders, ONE deep shadow (the panel),
 * cool grays (hue ~220), marked type hierarchy, outline chips, near-zero blur,
 * dry and fast motion (140-180ms). Everything themes through `--nx-*` custom
 * properties on the root; the client contract is `--nx-accent` (+ optional
 * `--nx-on-accent`). Grays, radii, spacing and motion are system-owned.
 *
 * Namespaced under `.nexor-chat` so nothing leaks into the host page.
 * Animations are GPU-friendly (transform + opacity only).
 */
export const widgetCss = (
  accent: string,
  accentText: string,
  shape: "rounded" | "square" = "rounded",
): string => {
  const radii =
    shape === "square"
      ? { xs: "2px", sm: "2px", md: "3px", lg: "4px", panel: "6px", bubble: "3px", bubbleCorner: "1px" }
      : { xs: "6px", sm: "10px", md: "12px", lg: "16px", panel: "16px", bubble: "10px", bubbleCorner: "4px" };
  return `
.nexor-chat,
.nexor-chat * {
  box-sizing: border-box;
  font-family: var(--nx-font);
}

.nexor-chat {
  /* ── Contract ── */
  --nx-accent: ${accent};
  --nx-on-accent: ${accentText};
  --nx-accent-text: var(--nx-on-accent); /* legacy alias */
  --nx-font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;

  /* ── Spacing (base 4px) ── */
  --nx-space-1: 4px;  --nx-space-2: 8px;  --nx-space-3: 12px;
  --nx-space-4: 16px; --nx-space-5: 20px; --nx-space-6: 24px;
  --nx-space-8: 32px;

  /* ── Cool grays (hue ~220) ── */
  --nx-gray-0: #ffffff;  --nx-gray-25: #fcfcfd; --nx-gray-50: #f8f9fb;
  --nx-gray-100: #eef0f4; --nx-gray-200: #e3e6ec; --nx-gray-300: #cfd4dd;
  --nx-gray-400: #9aa1ae; --nx-gray-500: #6c7484; --nx-gray-600: #4d5464;
  --nx-gray-700: #363c49; --nx-gray-900: #14171f;

  /* ── Accent derivatives (never hardcoded variants) ── */
  --nx-accent-hover: color-mix(in oklch, var(--nx-accent) 88%, black);
  --nx-accent-soft: color-mix(in oklch, var(--nx-accent) 8%, white);
  --nx-accent-ring: color-mix(in oklch, var(--nx-accent) 35%, transparent);

  /* ── Shadows: one deep shadow for the floating panel; borders do the rest ── */
  --nx-shadow-1: 0 1px 2px rgba(16,24,40,.07);
  --nx-shadow-2: 0 3px 8px -2px rgba(16,24,40,.14);
  --nx-shadow-3: 0 24px 56px -12px rgba(16,24,40,.28);

  /* ── Radii (depend on \`shape\`) ── */
  --nx-radius-xs: ${radii.xs}; --nx-radius-sm: ${radii.sm}; --nx-radius-md: ${radii.md};
  --nx-radius-lg: ${radii.lg}; --nx-radius-full: 999px;
  --nx-radius-panel: ${radii.panel}; --nx-radius-bubble: ${radii.bubble}; --nx-radius-bubble-corner: ${radii.bubbleCorner};

  /* ── Motion: dry, fast ── */
  --nx-ease-out: cubic-bezier(0.2, 0.9, 0.3, 1);
  --nx-ease-in-out: cubic-bezier(0.65, 0, 0.35, 1);
  --nx-dur-fast: 140ms;
  --nx-dur-base: 180ms;

  position: fixed;
  z-index: 2147483640;
  bottom: 20px;
  right: 20px;
  font-size: 14.5px;
  line-height: 1.45;
  color: var(--nx-gray-900);
}

.nexor-chat[data-position="bottom-left"] {
  right: auto;
  left: 20px;
}

.nexor-chat[data-restyling="true"],
.nexor-chat[data-restyling="true"] * {
  transition: none !important;
  animation: none !important;
}

/* ── Launcher wrapper (anchors the optional speed-dial fan) ───────── */
.nexor-chat__launcher-wrap {
  position: relative;
  display: inline-flex;
}

/* ── Launcher bubble ─────────────────────────────────────────────── */
.nexor-chat__launcher {
  width: 56px;
  height: 56px;
  border-radius: var(--nx-radius-full);
  background: var(--nx-accent);
  color: var(--nx-on-accent);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--nx-shadow-2), 0 0 0 1px rgba(16,24,40,.06);
  transition: transform var(--nx-dur-fast) var(--nx-ease-out),
              box-shadow var(--nx-dur-fast) var(--nx-ease-out),
              background var(--nx-dur-fast) var(--nx-ease-in-out);
  padding: 0;
  position: relative;
  animation: nexor-launcher-in var(--nx-dur-base) var(--nx-ease-out) both;
}
.nexor-chat__launcher:hover {
  background: var(--nx-accent-hover);
  transform: translateY(-1px);
}
.nexor-chat__launcher:active {
  transform: translateY(0) scale(0.96);
  transition-duration: 80ms;
}
.nexor-chat__launcher:focus-visible {
  outline: 2px solid var(--nx-accent);
  outline-offset: 2px;
}
.nexor-chat[data-open="true"] .nexor-chat__launcher {
  transform: scale(0.88);
  opacity: 0.7;
}
/* While the chat panel is open the launcher is dormant: no hover fan, no
   clicks. The visitor closes the chat (header X) to reach the channels again. */
.nexor-chat[data-open="true"] .nexor-chat__launcher-wrap {
  pointer-events: none;
}
.nexor-chat__launcher svg { width: 24px; height: 24px; transition: transform var(--nx-dur-base) var(--nx-ease-in-out); }

.nexor-chat__unread {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  background: #dc2626;
  color: #fff;
  border-radius: var(--nx-radius-full);
  font-size: 11px;
  font-weight: 700;
  padding: 0 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  animation: nexor-pop-in var(--nx-dur-base) var(--nx-ease-out) both;
}

@keyframes nexor-launcher-in {
  from { transform: translateY(12px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
@keyframes nexor-pop-in {
  from { transform: scale(0.5); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
}

/* ── Speed-dial channel fan ───────────────────────────────────────── */
.nexor-chat__channels {
  position: absolute;
  bottom: 100%;
  right: 0;
  padding-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: var(--nx-space-3);
  align-items: flex-end;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateY(8px);
  transition: opacity var(--nx-dur-base) var(--nx-ease-out),
              transform var(--nx-dur-base) var(--nx-ease-out),
              visibility var(--nx-dur-base);
}
.nexor-chat[data-position="bottom-left"] .nexor-chat__channels {
  right: auto;
  left: 0;
  align-items: flex-start;
}

@media (hover: hover) and (pointer: fine) {
  .nexor-chat__launcher-wrap:hover .nexor-chat__channels {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
  }
}
.nexor-chat__launcher-wrap[data-expanded="true"] .nexor-chat__channels {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateY(0);
}

.nexor-chat__channel-item {
  display: flex;
  align-items: center;
  gap: 10px;
  transform: translateY(6px);
  opacity: 0;
  transition: transform var(--nx-dur-base) var(--nx-ease-out), opacity var(--nx-dur-base) var(--nx-ease-out);
}
.nexor-chat[data-position="bottom-left"] .nexor-chat__channel-item {
  flex-direction: row-reverse;
}
.nexor-chat__launcher-wrap[data-expanded="true"] .nexor-chat__channel-item,
.nexor-chat__launcher-wrap:hover .nexor-chat__channel-item {
  transform: translateY(0);
  opacity: 1;
}
.nexor-chat__channel-item:nth-last-child(1) { transition-delay: 0ms; }
.nexor-chat__channel-item:nth-last-child(2) { transition-delay: 30ms; }
.nexor-chat__channel-item:nth-last-child(3) { transition-delay: 60ms; }
.nexor-chat__channel-item:nth-last-child(4) { transition-delay: 90ms; }
.nexor-chat__channel-item:nth-last-child(5) { transition-delay: 120ms; }

.nexor-chat__channel {
  width: 46px;
  height: 46px;
  border-radius: var(--nx-radius-full);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: var(--nx-accent);
  color: var(--nx-on-accent);
  box-shadow: var(--nx-shadow-2), 0 0 0 1px rgba(16,24,40,.06);
  text-decoration: none;
  transition: transform var(--nx-dur-fast) var(--nx-ease-out), filter var(--nx-dur-fast) var(--nx-ease-out);
}
.nexor-chat__channel:hover {
  transform: scale(1.06);
  filter: brightness(1.05);
}
.nexor-chat__channel:active { transform: scale(0.95); }
.nexor-chat__channel:focus-visible { outline: 2px solid var(--nx-accent); outline-offset: 2px; }
.nexor-chat__channel svg { width: 24px; height: 24px; display: block; }

/* Brand colours win over the accent where a channel has a recognisable hue. */
.nexor-chat__channel--whatsapp { background: #25d366; color: #fff; }
.nexor-chat__channel--instagram {
  background: radial-gradient(circle at 30% 110%, #fdf497 0%, #fd5949 45%, #d6249f 70%, #285aeb 100%);
  color: #fff;
}

.nexor-chat__channel-label {
  background: var(--nx-gray-0);
  color: var(--nx-gray-700);
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: var(--nx-radius-xs);
  border: 1px solid var(--nx-gray-200);
  white-space: nowrap;
  box-shadow: var(--nx-shadow-1);
}

/* ── Panel: the ONE deep shadow lives here ────────────────────────── */
.nexor-chat__panel {
  position: absolute;
  bottom: 72px;
  right: 0;
  width: 384px;
  max-width: calc(100vw - 32px);
  height: min(660px, calc(100vh - 120px));
  max-height: calc(100vh - 120px);
  background: var(--nx-gray-0);
  border: 1px solid var(--nx-gray-200);
  border-radius: var(--nx-radius-panel);
  box-shadow: var(--nx-shadow-3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateY(8px) scale(0.98);
  opacity: 0;
  pointer-events: none;
  transition: transform var(--nx-dur-base) var(--nx-ease-out),
              opacity var(--nx-dur-base) var(--nx-ease-out);
  transform-origin: bottom right;
  visibility: hidden;
}
.nexor-chat[data-position="bottom-left"] .nexor-chat__panel {
  right: auto;
  left: 0;
  transform-origin: bottom left;
}
.nexor-chat[data-open="true"] .nexor-chat__panel {
  transform: translateY(0) scale(1);
  opacity: 1;
  pointer-events: auto;
  visibility: visible;
}

/* ── Header: sober, white, 1px rule. No accent gradient. ──────────── */
.nexor-chat__header {
  background: var(--nx-gray-0);
  color: var(--nx-gray-900);
  min-height: 64px;
  padding: var(--nx-space-3) var(--nx-space-4);
  border-bottom: 1px solid var(--nx-gray-100);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nx-space-3);
  position: relative;
  flex: none;
}

/* Avatar: initials (or client logo) in a circle; presence IS the ring. */
.nexor-chat__avatar {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: var(--nx-radius-full);
  background: var(--nx-gray-900);
  color: var(--nx-gray-0);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  overflow: visible;
  position: relative;
  box-shadow: 0 0 0 1px var(--nx-gray-200), 0 1px 3px rgba(0,0,0,0.08);
  background: color-mix(in srgb, var(--nx-accent) 14%, #fff);
  color: color-mix(in srgb, var(--nx-accent) 80%, #000);
}
.nexor-chat__avatar[data-presence="away"]::after { background: #f59e0b; }
.nexor-chat__avatar[data-presence="offline"]::after { background: var(--nx-gray-400); }
.nexor-chat__avatar::after {
  content: "";
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: #16a34a;
  border: 2px solid var(--nx-gray-0);
}
.nexor-chat__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: inherit;
}

.nexor-chat__title {
  color: var(--nx-gray-900);
  font-weight: 750;
  font-size: 18px;
  line-height: 24px;
  letter-spacing: -0.02em;
  margin: 0;
}
.nexor-chat__subtitle {
  color: var(--nx-gray-500);
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  margin: 1px 0 0;
}
/* Presence lives in the avatar ring — the loose green dot is retired. */
.nexor-chat__status-dot { display: none; }

.nexor-chat__back {
  background: transparent;
  width: 32px;
  height: 32px;
  flex: none;
  display: none;
  place-items: center;
  border: none;
  border-radius: var(--nx-radius-xs);
  color: var(--nx-gray-400);
  cursor: pointer;
  padding: 0;
  margin-right: 2px;
  transition: background var(--nx-dur-fast) var(--nx-ease-in-out), color var(--nx-dur-fast) var(--nx-ease-in-out);
}
.nexor-chat__back:hover { background: var(--nx-gray-50); color: var(--nx-gray-700); }
.nexor-chat__back svg { width: 18px; height: 18px; display: block; }
.nexor-chat__header[data-show-back="true"] .nexor-chat__back { display: grid; }

.nexor-chat__close,
.nexor-chat__restart {
  background: transparent;
  width: 32px;
  height: 32px;
  flex: none;
  display: grid;
  place-items: center;
  border: none;
  border-radius: var(--nx-radius-xs);
  color: var(--nx-gray-400);
  cursor: pointer;
  padding: 0;
  transition: background var(--nx-dur-fast) var(--nx-ease-in-out), color var(--nx-dur-fast) var(--nx-ease-in-out);
}
.nexor-chat__close:hover,
.nexor-chat__restart:hover { background: var(--nx-gray-50); color: var(--nx-gray-700); }
.nexor-chat__close:active,
.nexor-chat__restart:active { background: var(--nx-gray-100); }
.nexor-chat__close svg { width: 18px; height: 18px; display: block; }
.nexor-chat__restart svg { width: 16px; height: 16px; display: block; }
/* Keep title/subtitle left; actions on the right. Avatar is flex:none above. */
.nexor-chat__header > div:not(.nexor-chat__avatar) { margin-right: auto; min-width: 0; }
/* Nothing to reset on the capture screen — hide it there. */
.nexor-chat__panel[data-view="capture"] .nexor-chat__restart { display: none; }
/* The form header (contact view) is not a live conversation. */
.nexor-chat__panel[data-view="contact"] .nexor-chat__avatar { display: none; }

.nexor-chat__agent-caption {
  display: none;
  font-size: 12px;
  color: inherit;
  opacity: 0.62;
  margin: 1px 0 0;
  line-height: 1.3;
}
.nexor-chat__panel[data-view="chat"] .nexor-chat__agent-caption { display: block; }
/* Home en estado captura: el card se transforma en el form, en la misma pagina. */
.nexor-chat__home[data-state="capture"] .nexor-chat__home-card { display: none; }
.nexor-chat__home .nexor-chat__capture {
  margin: 0;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06), 0 12px 32px rgba(0,0,0,0.14);
}
/* En el chat el header es la persona: nombre + empresa, sin subtitulo. */
.nexor-chat__panel[data-view="chat"] .nexor-chat__subtitle { display: none; }
.nexor-chat__panel[data-view="chat"] .nexor-chat__title { font-size: 16px; }

/* ── Home view (Intercom-style landing) ───────────────────────────── */
.nexor-chat__panel[data-view="home"] .nexor-chat__header,
.nexor-chat__panel[data-view="home"] .nexor-chat__body,
.nexor-chat__panel[data-view="home"] .nexor-chat__composer,
.nexor-chat__panel[data-view="home"] .nexor-chat__capture-view,
.nexor-chat__panel[data-view="home"] .nexor-chat__contact-view {
  display: none;
}
/* El footer si vive en el home — cierra el layout y da la atribucion. */
.nexor-chat__panel[data-view="home"] .nexor-chat__footer {
  margin-top: auto;
  background: transparent;
  border-top: none;
}
.nexor-chat__panel:not([data-view="home"]) .nexor-chat__home {
  display: none;
}
.nexor-chat__home {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  background: var(--nx-gray-0);
  animation: nexor-fade-in var(--nx-dur-base) var(--nx-ease-out) both;
}
.nexor-chat__home-hero {
  position: relative;
  padding: 28px 20px 72px;
  background: linear-gradient(
    160deg,
    color-mix(in oklch, var(--nx-accent) 100%, transparent),
    color-mix(in oklch, var(--nx-accent) 78%, black)
  );
  color: #fff;
  flex: none;
}
.nexor-chat__home-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: var(--nx-radius-xs);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  cursor: pointer;
  transition: background var(--nx-dur-fast) var(--nx-ease-in-out);
}
.nexor-chat__home-close:hover { background: rgba(255, 255, 255, 0.3); }
.nexor-chat__home-close svg { width: 18px; height: 18px; display: block; }
.nexor-chat__home-avatar {
  width: 40px;
  height: 40px;
  border-radius: var(--nx-radius-full);
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: var(--nx-space-4);
  overflow: hidden;
  box-shadow: 0 0 0 2px rgba(255,255,255,0.45), 0 4px 14px rgba(0,0,0,0.18);
}
.nexor-chat__home-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.nexor-chat__home-greeting {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.25;
  color: rgba(255, 255, 255, 0.7);
}
.nexor-chat__home-title {
  margin: 0;
  font-size: 28px;
  font-weight: 750;
  line-height: 1.25;
  color: #fff;
  letter-spacing: -0.01em;
}
.nexor-chat__home-cards {
  margin-top: -48px;
  padding: 0 16px var(--nx-space-5);
  display: flex;
  flex-direction: column;
  gap: var(--nx-space-3);
  position: relative;
}
.nexor-chat__home-card {
  background: var(--nx-gray-0);
  border-radius: var(--nx-radius-lg);
  box-shadow: var(--nx-shadow-2), var(--nx-shadow-1);
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  width: 100%;
}
.nexor-chat__home-message {
  display: flex;
  align-items: center;
  gap: var(--nx-space-3);
  padding: var(--nx-space-4);
  transition: transform var(--nx-dur-fast) var(--nx-ease-out);
}
.nexor-chat__home-message:hover { transform: translateY(-1px); }
.nexor-chat__home-message:active { transform: translateY(0) scale(0.99); }
.nexor-chat__home-message-copy { flex: 1; min-width: 0; }
.nexor-chat__home-message-title {
  margin: 0;
  font-size: 14px;
  font-weight: 650;
  color: var(--nx-gray-900);
}
.nexor-chat__home-message-subtitle {
  margin: 2px 0 0;
  font-size: 12.5px;
  color: var(--nx-gray-500);
}
.nexor-chat__home-message-icon {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: var(--nx-radius-full);
  background: var(--nx-accent-soft);
  color: var(--nx-accent);
  display: flex;
  align-items: center;
  justify-content: center;
}
.nexor-chat__home-message-icon svg { width: 18px; height: 18px; }
.nexor-chat__home-suggestions {
  padding: var(--nx-space-2) 0;
}
.nexor-chat__home-suggestion-row {
  display: flex;
  align-items: center;
  gap: var(--nx-space-2);
  padding: 13px var(--nx-space-4);
  border: none;
  background: transparent;
  border-bottom: 1px solid var(--nx-gray-100);
  width: 100%;
  text-align: left;
  font: inherit;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--nx-gray-700);
  cursor: pointer;
  transition: background var(--nx-dur-fast) var(--nx-ease-in-out);
}
.nexor-chat__home-suggestions .nexor-chat__home-suggestion-row:last-child { border-bottom: none; }
.nexor-chat__home-suggestion-row:hover { background: var(--nx-gray-50); }
.nexor-chat__home-suggestion-text { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nexor-chat__home-suggestion-row svg { flex: none; width: 16px; height: 16px; color: var(--nx-gray-400); }

/* ── Body / messages ──────────────────────────────────────────────── */
/* Llegar al tope/fondo de un scroller del widget no debe encadenar el scroll
   a la pagina host (en mobile eso mueve la pagina detras del panel full). */
.nexor-chat__body,
.nexor-chat__home,
.nexor-chat__capture-view {
  overscroll-behavior: contain;
}
.nexor-chat__body {
  flex: 1;
  overflow-y: auto;
  padding: var(--nx-space-4);
  background: var(--nx-gray-25);
  display: flex;
  flex-direction: column;
  gap: var(--nx-space-2);
  scroll-behavior: smooth;
  scrollbar-gutter: stable;
}
.nexor-chat__body { scrollbar-width: thin; scrollbar-color: var(--nx-gray-300) transparent; }
.nexor-chat__body::-webkit-scrollbar { width: 5px; }
.nexor-chat__body::-webkit-scrollbar-thumb {
  background: var(--nx-gray-300);
  border-radius: var(--nx-radius-full);
}

.nexor-chat__msg {
  max-width: 82%;
  padding: 10px 14px;
  border-radius: var(--nx-radius-bubble);
  font-size: 14.5px;
  line-height: 21px;
  white-space: pre-wrap;
  word-wrap: break-word;
  animation: nexor-msg-in 160ms var(--nx-ease-out) both;
  will-change: transform, opacity;
}
.nexor-chat__msg--bot {
  background: var(--nx-gray-100);
  color: var(--nx-gray-900);
  align-self: flex-start;
  border-bottom-left-radius: var(--nx-radius-bubble-corner);
}
.nexor-chat__msg--user {
  background: color-mix(in srgb, var(--nx-accent) 90%, #fff);
  color: var(--nx-on-accent);
  font-weight: 450;
  align-self: flex-end;
  border-bottom-right-radius: var(--nx-radius-bubble-corner);
}
.nexor-chat__msg--user a { color: color-mix(in oklch, var(--nx-on-accent) 85%, var(--nx-accent)); text-decoration: underline; }
.nexor-chat__msg--system {
  background: transparent;
  color: var(--nx-gray-500);
  font-size: 12px;
  align-self: center;
  text-align: center;
  max-width: 100%;
  animation-name: nexor-fade-in;
}
.nexor-chat__msg-time {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  line-height: 16px;
  opacity: 0.6;
  text-align: right;
}
.nexor-chat__msg--bot .nexor-chat__msg-time { text-align: left; color: var(--nx-gray-500); }
/* Clickable links in message text. Bot bubbles use the accent; user-bubble
   links inherit the .nexor-chat__msg--user a rule above. */
.nexor-chat__msg-link {
  color: var(--nx-accent);
  text-decoration: underline;
  word-break: break-all;
}

/* File Library attachment: the image itself is the bubble. */
.nexor-chat__msg--media {
  padding: 0;
  background: transparent;
  border: none;
  box-shadow: none;
  overflow: hidden;
}
.nexor-chat__media-img {
  display: block;
  max-width: 100%;
  max-height: 260px;
  width: auto;
  height: auto;
  border-radius: var(--nx-radius-sm);
  border-bottom-left-radius: 4px;
  border: 1px solid var(--nx-gray-200);
  object-fit: cover;
}
.nexor-chat__media-doc {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border-radius: var(--nx-radius-sm);
  border-bottom-left-radius: 4px;
  background: var(--nx-gray-0);
  border: 1px solid var(--nx-gray-200);
  color: var(--nx-accent);
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
}
.nexor-chat__media-doc::before { content: "\\1F4CE"; }

@keyframes nexor-msg-in {
  from { transform: translateY(4px); opacity: 0; }
  to   { transform: translateY(0);   opacity: 1; }
}
@keyframes nexor-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* ── Suggested-question chips: outline pills, user-side aligned ───── */
.nexor-chat__suggestions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--nx-space-2);
  margin-top: var(--nx-space-1);
  animation: nexor-fade-in var(--nx-dur-base) var(--nx-ease-out) both;
}
.nexor-chat__suggestion {
  background: var(--nx-gray-0);
  border: 1px solid var(--nx-gray-300);
  border-radius: var(--nx-radius-full);
  color: var(--nx-gray-700);
  font: inherit;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  padding: 8px 14px;
  cursor: pointer;
  text-align: right;
  max-width: 100%;
  transition: border-color var(--nx-dur-fast) var(--nx-ease-in-out),
              background var(--nx-dur-fast) var(--nx-ease-in-out),
              color var(--nx-dur-fast) var(--nx-ease-in-out);
  animation: nexor-msg-in 160ms var(--nx-ease-out) both;
}
.nexor-chat__suggestion:nth-child(1) { animation-delay: 0ms; }
.nexor-chat__suggestion:nth-child(2) { animation-delay: 40ms; }
.nexor-chat__suggestion:nth-child(3) { animation-delay: 80ms; }
.nexor-chat__suggestion:nth-child(4) { animation-delay: 120ms; }
.nexor-chat__suggestion:hover {
  border-color: color-mix(in oklch, var(--nx-accent) 40%, var(--nx-gray-300));
  background: var(--nx-accent-soft);
  color: var(--nx-gray-900);
}
.nexor-chat__suggestion:active { background: color-mix(in oklch, var(--nx-accent) 14%, white); }
.nexor-chat__suggestion:focus-visible { outline: 2px solid var(--nx-accent); outline-offset: 2px; }

.nexor-chat__typing {
  align-self: flex-start;
  background: var(--nx-gray-100);
  border-radius: var(--nx-radius-sm);
  border-bottom-left-radius: 4px;
  padding: 12px 14px;
  display: inline-flex;
  gap: 4px;
  animation: nexor-msg-in 160ms var(--nx-ease-out) both;
}
.nexor-chat__typing span {
  width: 6px; height: 6px; border-radius: var(--nx-radius-full);
  background: var(--nx-gray-400);
  display: inline-block;
  animation: nexor-blink 1.2s infinite var(--nx-ease-in-out);
}
.nexor-chat__typing span:nth-child(2) { animation-delay: 0.16s; }
.nexor-chat__typing span:nth-child(3) { animation-delay: 0.32s; }
@keyframes nexor-blink {
  0%, 60%, 100% { opacity: 0.35; transform: scale(0.7); }
  30%           { opacity: 1;    transform: scale(1); }
}

/* ── Resume banner (returning visitor) ────────────────────────────── */
.nexor-chat__resume {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: var(--nx-gray-0);
  border: 1px solid var(--nx-gray-200);
  border-radius: var(--nx-radius-sm);
  padding: 8px 12px;
  margin-bottom: var(--nx-space-1);
  animation: nexor-fade-in var(--nx-dur-base) var(--nx-ease-out) both;
}
.nexor-chat__resume-text {
  font-size: 12px;
  font-weight: 500;
  color: var(--nx-gray-500);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nexor-chat__resume-reset {
  flex: none;
  background: transparent;
  border: none;
  color: var(--nx-gray-700);
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: var(--nx-radius-xs);
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color var(--nx-dur-fast) var(--nx-ease-in-out);
}
.nexor-chat__resume-reset:hover { color: var(--nx-accent); }
.nexor-chat__resume-reset:active { opacity: 0.6; }

/* ── Capture as its own full-panel view ───────────────────────────── */
.nexor-chat__capture-view {
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding: 0 0 24px;
  background-color: var(--nx-gray-50);
  background-image: linear-gradient(160deg,
    var(--nx-accent) 0%,
    color-mix(in srgb, var(--nx-accent) 78%, #000) 100%);
  background-size: 100% 236px;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  animation: nexor-fade-in var(--nx-dur-base) var(--nx-ease-out) both;
}
.nexor-chat__panel[data-view="capture"] {
  height: auto;
}
.nexor-chat__panel[data-view="chat"] .nexor-chat__capture-view { display: none; }
.nexor-chat__panel[data-view="capture"] .nexor-chat__body,
.nexor-chat__panel[data-view="capture"] .nexor-chat__composer { display: none; }

/* ── Lead capture form ────────────────────────────────────────────── */
.nexor-chat__capture {
  background: var(--nx-gray-0);
  border: none;
  border-radius: var(--nx-radius-lg);
  padding: 20px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: -56px 16px 0;
  animation: nexor-msg-in var(--nx-dur-base) var(--nx-ease-out) both;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06), 0 12px 32px rgba(0,0,0,0.12);
}
.nexor-chat__capture-hero {
  font-size: 24px;
  font-weight: 750;
  letter-spacing: -0.02em;
  line-height: 1.25;
  color: #fff;
  margin: 0;
  padding: 36px 20px 0;
  min-height: 172px;
  flex: none;
}
.nexor-chat__capture-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--nx-gray-700);
  margin: 0;
}
.nexor-chat__capture-row {
  display: flex;
  gap: var(--nx-space-2);
}
.nexor-chat__capture-row > * { flex: 1; }
.nexor-chat__input,
.nexor-chat__capture input {
  font: inherit;
  font-size: 16px; /* <16px dispara zoom-on-focus en iOS */
  padding: 13px 14px;
  border: 1px solid var(--nx-gray-200);
  border-radius: 10px;
  background: var(--nx-gray-0);
  box-shadow: 0 1px 2px rgba(16,24,40,0.04);
  color: var(--nx-gray-900);
  outline: none;
  width: 100%;
  transition: border-color var(--nx-dur-fast) var(--nx-ease-in-out), box-shadow var(--nx-dur-fast) var(--nx-ease-in-out);
}
.nexor-chat__input::placeholder,
.nexor-chat__capture input::placeholder { color: var(--nx-gray-400); }
/* Composer textarea: auto-grows (height managed in JS), 1px defined border. */
textarea.nexor-chat__input {
  font-size: 16px; /* <16px dispara zoom-on-focus en iOS (misma regla que capture) */
  background: transparent;
  border: none;
  outline: none !important;
  box-shadow: none !important;
  -webkit-appearance: none;
  appearance: none;
  border-radius: 0;
  -webkit-tap-highlight-color: transparent;
  display: block;
  resize: none;
  overflow-y: auto;
  line-height: 20px;
  max-height: 98px; /* ~5 lines */
  white-space: pre-wrap;
  word-break: break-word;
  padding: 9px 16px;
}
/* El pill del composer es la TARJETA (composer-card): el textarea nunca pinta
   fondo/borde/radio propio — el focus lo dibuja la tarjeta via :focus-within. */
.nexor-chat__input:focus:not(textarea),
.nexor-chat__capture input:focus {
  background: var(--nx-gray-0);
  border-color: var(--nx-gray-400);
  box-shadow: 0 1px 2px rgba(16,24,40,0.06), 0 0 0 4px rgba(16,24,40,0.04);
}
.nexor-chat__input--error,
.nexor-chat__capture input.nexor-chat__input--error {
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
}
.nexor-chat__capture-error {
  font-size: 12px;
  font-weight: 500;
  color: #dc2626;
  margin: 0;
}
.nexor-chat__capture-error[hidden] { display: none; }

/* Separate, affirmative SMS consent. This control is deliberately independent
   from the submit action: unchecked visitors can still start chat. */
.nexor-chat__sms-consent {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  color: var(--nx-gray-600);
  font-size: 11px;
  line-height: 1.45;
  cursor: pointer;
}
.nexor-chat__capture .nexor-chat__sms-consent input[type="checkbox"] {
  appearance: auto;
  -webkit-appearance: checkbox;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  min-width: 16px;
  padding: 0;
  margin: 1px 0 0;
  border: initial;
  border-radius: 3px;
  box-shadow: none;
  accent-color: var(--nx-accent);
}
.nexor-chat__sms-consent-copy { display: block; }
.nexor-chat__sms-consent a {
  color: var(--nx-gray-800);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.nexor-chat__sms-consent a:hover { color: var(--nx-accent); }

/* ── Phone field (flag + dial code selector joined to a digit input) ── */
.nexor-chat__phone {
  display: flex;
  align-items: stretch;
  width: 100%;
  border: 1px solid var(--nx-gray-200);
  border-radius: 10px;
  background: var(--nx-gray-0);
  box-shadow: 0 1px 2px rgba(16,24,40,0.04);
  overflow: hidden;
  transition: border-color var(--nx-dur-fast) var(--nx-ease-in-out), box-shadow var(--nx-dur-fast) var(--nx-ease-in-out);
}
.nexor-chat__phone:focus-within {
  border-color: var(--nx-gray-400);
  box-shadow: 0 1px 2px rgba(16,24,40,0.06), 0 0 0 4px rgba(16,24,40,0.04);
}
.nexor-chat__phone.nexor-chat__input--error {
  border-color: #dc2626;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
}
.nexor-chat__phone-country {
  flex: none;
  border: none;
  border-right: 1px solid var(--nx-gray-200);
  background: var(--nx-gray-50);
  color: var(--nx-gray-900);
  font: inherit;
  padding: 0 12px 0 10px;
  cursor: pointer;
  outline: none;
}
.nexor-chat__phone-number.nexor-chat__input {
  border: none;
  border-radius: 0;
  box-shadow: none;
  flex: 1;
  min-width: 0;
}

.nexor-chat__capture-submit {
  background: var(--nx-accent);
  color: var(--nx-on-accent);
  border: none;
  border-radius: var(--nx-radius-sm);
  padding: 12px 14px;
  font-size: 15px;
  margin-top: 4px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background var(--nx-dur-fast) var(--nx-ease-in-out), transform var(--nx-dur-fast) var(--nx-ease-out);
}
.nexor-chat__capture-submit:hover { background: var(--nx-accent-hover); }
.nexor-chat__capture-submit:active { transform: scale(0.98); }
.nexor-chat__capture-submit[disabled] { opacity: 0.55; cursor: not-allowed; }

/* ── Contact-request view (call me / email me) ────────────────────── */
.nexor-chat__contact-view {
  min-height: 0;
  /* Fill the panel between header and footer. Without this the view only ever
     grows to its content, so on the full-screen mobile panel the footer floats
     mid-screen with ~480px of dead white beneath it. */
  flex: 1;
  overflow-y: auto;
  padding: var(--nx-space-4);
  background: var(--nx-gray-25);
  display: flex;
  flex-direction: column;
  animation: nexor-fade-in var(--nx-dur-base) var(--nx-ease-out) both;
}
/* The form reuses .nexor-chat__capture, whose -56px top margin exists to lift
   the card over the CAPTURE view's gradient hero. This view has no hero, so
   that margin drags the first row 40px under the header where no scrolling can
   reach it, and the extra 16px card margin double-insets the form against the
   view's own padding. Reset both — same override the home view already does. */
.nexor-chat__contact-view .nexor-chat__capture {
  margin: 0;
}
.nexor-chat__panel[data-view="contact"] { height: auto; }
.nexor-chat__panel:not([data-view="contact"]) .nexor-chat__contact-view { display: none; }
.nexor-chat__panel[data-view="contact"] .nexor-chat__capture-view,
.nexor-chat__panel[data-view="contact"] .nexor-chat__body,
.nexor-chat__panel[data-view="contact"] .nexor-chat__composer { display: none; }
.nexor-chat__panel[data-view="contact"] .nexor-chat__restart { display: none; }
.nexor-chat__panel[data-view="contact"] .nexor-chat__status-dot { display: none; }

/* form <-> success swap, driven by the view's data-state */
.nexor-chat__contact-view[data-state="sent"] .nexor-chat__contact-form { display: none; }
.nexor-chat__contact-view:not([data-state="sent"]) .nexor-chat__contact-success { display: none; }

.nexor-chat__contact-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--nx-space-3);
  padding: 28px 12px 12px;
  animation: nexor-msg-in var(--nx-dur-base) var(--nx-ease-out) both;
}
.nexor-chat__contact-success-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--nx-radius-full);
  background: var(--nx-accent);
  color: var(--nx-on-accent);
}
.nexor-chat__contact-success-icon svg { width: 26px; height: 26px; }
.nexor-chat__contact-success-text {
  font-size: 14.5px;
  color: var(--nx-gray-600);
  margin: 0;
  line-height: 1.5;
}
.nexor-chat__contact-success .nexor-chat__capture-submit {
  margin-top: 4px;
  align-self: stretch;
}

/* ── Composer ─────────────────────────────────────────────────────── */
.nexor-chat__body-wrap { position: relative; flex: 1; display: flex; flex-direction: column; min-height: 0; }
.nexor-chat__composer-card {
  flex: 1;
  cursor: text;
  display: flex;
  flex-direction: column;
  background: var(--nx-gray-0);
  border: 1px solid var(--nx-gray-200);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06);
  padding: 4px 6px 6px;
  transition: border-color var(--nx-dur-fast) var(--nx-ease-in-out), box-shadow var(--nx-dur-fast) var(--nx-ease-in-out);
}
.nexor-chat__composer-card:focus-within {
  border-color: var(--nx-gray-400);
  box-shadow: 0 1px 2px rgba(16,24,40,0.06), 0 0 0 4px rgba(16,24,40,0.04);
}
.nexor-chat__composer-actions {
  cursor: text;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  padding: 2px 2px 0;
}
.nexor-chat__composer {
  position: relative;
  display: flex;
  gap: var(--nx-space-2);
  padding: var(--nx-space-3);
  background: var(--nx-gray-0);
  border-top: 1px solid var(--nx-gray-100);
  align-items: flex-end;
  flex: none;
  background: transparent;
  border-top: none;
  padding: 4px 12px 8px;
}
/* Los mensajes se disuelven al llegar al composer (fade + tinte del accent). */
.nexor-chat__composer::before {
  content: "";
  position: absolute;
  left: 0; right: 0;
  bottom: 100%;
  height: 36px;
  pointer-events: none;
  background: linear-gradient(to bottom,
    transparent 0%,
    color-mix(in srgb, var(--nx-accent) 2.5%, var(--nx-gray-0)) 78%,
    var(--nx-gray-0) 100%);
}
.nexor-chat__send {
  cursor: pointer;
  background: var(--nx-accent);
  color: var(--nx-on-accent);
  border: none;
  border-radius: var(--nx-radius-full);
  padding: 0;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  width: 32px;
  height: 32px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background var(--nx-dur-fast) var(--nx-ease-in-out), transform var(--nx-dur-fast) var(--nx-ease-out);
}
.nexor-chat__send:hover { background: var(--nx-accent-hover); }
.nexor-chat__send:active { transform: scale(0.92); }
.nexor-chat__send[disabled] {
  background: var(--nx-gray-200);
  color: var(--nx-gray-400);
  cursor: not-allowed;
  transform: none;
}
.nexor-chat__send svg {
  width: 18px;
  height: 18px;
  transition: transform var(--nx-dur-fast) var(--nx-ease-out);
}
.nexor-chat__send:not([disabled]):hover svg { transform: translateY(-1px); }

/* Small spinner inside the send button while a turn is in flight */
.nexor-chat__send--loading svg { display: none; }
.nexor-chat__send--loading::after {
  content: "";
  width: 14px;
  height: 14px;
  border-radius: var(--nx-radius-full);
  border: 2px solid currentColor;
  border-top-color: transparent;
  animation: nexor-spin 700ms linear infinite;
}
@keyframes nexor-spin {
  to { transform: rotate(360deg); }
}

.nexor-chat__footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 11px;
  line-height: 16px;
  font-weight: 500;
  color: var(--nx-gray-400);
  padding: 6px 6px 8px;
  background: var(--nx-gray-0);
  flex: none;
}
.nexor-chat__footer a {
  color: inherit;
  text-decoration: none;
  font-weight: 600;
}
.nexor-chat__brand {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;
  color: var(--nx-gray-600);
  transition: color var(--nx-dur-fast) var(--nx-ease-in-out);
}
.nexor-chat__brand:hover { color: var(--nx-gray-900); }
.nexor-chat__brand-logo {
  height: 14px;
  width: auto;
  display: block;
}
.nexor-chat__brand-tip {
  position: absolute;
  bottom: calc(100% + 7px);
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  background: var(--nx-gray-900);
  color: #fff;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.01em;
  padding: 5px 8px;
  border-radius: var(--nx-radius-xs);
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  box-shadow: var(--nx-shadow-2);
  transition: opacity var(--nx-dur-fast) var(--nx-ease-in-out), transform var(--nx-dur-fast) var(--nx-ease-out);
  z-index: 5;
}
.nexor-chat__brand-tip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-top-color: var(--nx-gray-900);
}
.nexor-chat__brand:hover .nexor-chat__brand-tip {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* ── Mobile: full-screen panel with an obvious close affordance ───── */
/* Full-screen en cualquier viewport de telefono: angosto (vertical, ventanas
   chicas) O bajo (telefono girado: 844x390 — el ancho no lo detecta, el alto
   si). El panel flotante de desktop en esos tamanos no cubre, queda cortado y
   deja la pagina scrolleando detras. */
@media (max-width: 768px), (max-height: 500px) {
  .nexor-chat__panel {
    position: fixed;
    inset: 0;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
    border: none;
    transform: translateY(100%);
  }
  .nexor-chat[data-position="bottom-left"] .nexor-chat__panel { left: 0; }
  .nexor-chat[data-open="true"] .nexor-chat__panel { transform: translateY(0); }
  .nexor-chat__panel[data-view="capture"] {
    height: 100vh;
    height: 100dvh;
  }

  /* Hide the launcher (and any open fan) while the full-screen panel is up. */
  .nexor-chat[data-open="true"] .nexor-chat__launcher-wrap {
    opacity: 0;
    pointer-events: none;
  }

  /* Roomier header + a bigger, unmistakable close target on touch. */
  .nexor-chat__header {
    padding: var(--nx-space-4);
    padding-top: max(16px, env(safe-area-inset-top));
  }
  .nexor-chat__close {
    width: 40px;
    height: 40px;
    background: var(--nx-gray-50);
  }
  .nexor-chat__close svg { width: 22px; height: 22px; }

  /* Keep the composer clear of the home-indicator / gesture bar. */
  .nexor-chat__composer {
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }

  .nexor-chat__home-hero {
    padding: max(20px, env(safe-area-inset-top)) 20px 88px;
  }
  .nexor-chat__home-cards {
    padding-left: 16px;
    padding-right: 16px;
  }
}

/* Respect users who request reduced motion. */
@media (prefers-reduced-motion: reduce) {
  .nexor-chat,
  .nexor-chat * {
    animation-duration: 0.001ms !important;
    transition-duration: 0.001ms !important;
  }
}
`;
};
